import * as _ from 'lodash-es'
import { useEffect, useState } from "react"
import { useQueryNext } from '../../componentsF/hooksF/useQueryNextF.js'
import { Box, Spinner} from '@chakra-ui/react'
import { GameContext } from './GameContextF.jsx'
import { useAction } from './useAction.js'
import { GetOldGameStateQuery } from './TelDrawGame/OldGameViewer/OldGamePageF.jsx'

import { ErrLoadingWrap } from '../../componentsF/ErrLoadingF.jsx'
import { useGameRoom } from './common/GenericGamePage.jsx'
import { DateTime } from 'luxon'

const commonProps = {
  rounded: 'full', bg: 'lightgray', color: 'black', fontSize: '4xl'
}

export const RoundedButton = (/** @type {{children?: import('react').ReactNode, isLoading?: boolean, loading?: boolean, isDisabled?: boolean, [key: string]: any}} */ {children, isLoading, loading, isDisabled, ...props}) => {
  isLoading ||= loading
  let {onClick} = props
  const disabled = isLoading || isDisabled
  if (disabled) onClick = null
  return <Box {...{cursor: onClick && 'pointer', opacity: disabled ? 0.6 : 1, pointerEvents: disabled ? 'none' : undefined, ...commonProps, ...props}}>
    {children}
    {isLoading && <>&nbsp;<Spinner /></>} 
  </Box>
}

export const GameActionBtn = (/** @type {{actionH: Record<string, any>, reqConfirmStr?: string, children?: import('react').ReactNode, [key: string]: any}} */ {actionH, reqConfirmStr, ...props}) => {
  const {loading, onClick: _onClick} = useAction(actionH)
  const [confirming, setConfirming] = useState(false)
  const [confirmTimeout, setConfirmTimeout] = useState(/** @type {ReturnType<typeof setTimeout> | null} */ (null))
  const onClick = () => {
    if (reqConfirmStr && !confirming) {
      setConfirming(true)
      setConfirmTimeout(setTimeout(() => setConfirming(false), 3000))
      return
    }
    if (confirmTimeout) clearTimeout(confirmTimeout)
    setConfirming(false)
    _onClick?.()
  }
  const confirmBg = confirming ? 'red' : props.bg
  return <RoundedButton {...{loading, onClick, ...props, bg: confirmBg}}>
    {confirming ? (reqConfirmStr) : null}
    {!confirming ? props.children : null}
  </RoundedButton>
}

const FetchGameInside = ({gameRoom, data, refetch, children}) => {
  const {gqGame: {gqGameState, ...restH}} = data

  // gqGameState can be null on long-poll timeout - trigger immediate refetch
  const {cursorHash} = gqGameState || {}

  const fetchGameUpdate = async () => {
    await refetch({lastCursorHash: cursorHash})
  }

  // On null gqGameState (timeout), trigger refetch and show loading state
  useEffect(() => {
    if (!gqGameState) fetchGameUpdate()
  }, [gqGameState])

  const {playerAlias} = _.get(gqGameState, ['myState', 'publicState'], {})

  const isHost = gqGameState?.isHost ?? false

  const [isKickingPlayer, setIsKickingPlayer] = useState(false)
  const value = {
    ...(gqGameState || {}), ...restH,
    playerAlias,
    isKickingPlayer, setIsKickingPlayer,
    isHost, gameRoom, fetchGameUpdate,
  }

  useEffect(() => {
    const ii = setTimeout(fetchGameUpdate, 1000)
    return () => clearTimeout(ii)
  }, [cursorHash])

  return <GameCtxWrap {...{children, value}} />
}

export const GameCtxWrap = ({children, value}) => {
  value.gqActionsHistA = _.map(value.gqActionsHistA, (row) => {
    return {
      ...row,
      actionAtDt: row.actionAtDt || DateTime.fromMillis(row.actionAtMs),
    }
  })
  
  return <GameContext.Provider {...{value}}>
    {children}
  </GameContext.Provider>
}

export const FetchGameState = ({children}) => {
  const {gameRoom} = useGameRoom()

  const {data, loading, error, refetch} = useQueryNext(GetOldGameStateQuery, {
    variables: {gameRoom},
    notifyOnNetworkStatusChange: true, // without this can get stuck in wrong state after return to tab
    fetchPolicy: 'cache-and-network',
    pollInterval: 5 * 60 * 1000, // attempt to fix gma's getting stuck
  })

  const [lastData, setLastData] = useState()
  useEffect(() => {
    // Only save data if gqGameState is present (null on long-poll timeout)
    if (data?.gqGame?.gqGameState) {
      setLastData(data)
    }
    return () => {}
  }, [data])

  return <ErrLoadingWrap {...{error, data: lastData}}>
    <FetchGameInside {...{data: lastData, gameRoom, refetch}}>
      {children}
    </FetchGameInside>
  </ErrLoadingWrap>
}